import type { BillingPeriod, SubscriptionPlanData } from '@/features/subscription/types';
import type {
  SubscriptionPlanPeriodicity as BackendSubscriptionPlanPeriodicity,
  GetAvailableSubscriptionPlansQuery,
  SubscriptionPlanPeriodicity,
} from '@/shared/hooks/queries/generated';

// Types dérivés des queries GraphQL
type BackendSubscriptionPlanItem = GetAvailableSubscriptionPlansQuery['getAvailableSubscriptionPlans'][0];

// Mapping de la périodicité backend vers frontend
export const mapBackendPeriodicityToFrontend = (periodicity: BackendSubscriptionPlanPeriodicity): BillingPeriod => {
  switch (periodicity) {
    case 'monthly':
      return 'monthly';
    case 'yearly':
      return 'yearly';
    default:
      return 'monthly';
  }
};

// Mapper un plan d'abonnement backend vers SubscriptionPlanData complet
export const mapBackendSubscriptionPlanToData = ({
  plan,
  currentPlanId,
  usedHadSuscribed,
  billingPeriod,
  currentPlanBillingPeriod,
  handleSubscribe,
  handleCancel,
}: {
  plan: BackendSubscriptionPlanItem;
  currentPlanId: string | null | undefined;
  usedHadSuscribed: boolean;
  billingPeriod: BillingPeriod;
  currentPlanBillingPeriod: BillingPeriod | null | undefined;
  handleSubscribe: (planId: string, periodicity: SubscriptionPlanPeriodicity) => Promise<void>;
  handleCancel: () => Promise<void>;
}): SubscriptionPlanData => {
  // Détecter si c'est un downgrade (même plan + passage d'annuel vers mensuel)
  const isDowngrade = currentPlanId === plan.id && currentPlanBillingPeriod === 'yearly' && billingPeriod === 'monthly';

  return {
    id: plan.id,
    name: plan.name,
    priceMonthly: plan.priceMonthly ?? 0,
    priceYearly: plan.priceYearly ?? 0,
    catalogAnalysisQuotas: plan.catalogAnalysisQuotas,
    profileAnalysisQuotas: plan.profileAnalysisQuotas,
    trialPeriodDays: !usedHadSuscribed ? plan.trialPeriodDays : 0,
    isCurrentPlan: currentPlanId === plan.id && currentPlanBillingPeriod === billingPeriod,
    isDowngrade,
    handleSubscribe: async () => {
      await handleSubscribe(plan.id, billingPeriod);
    },
    handleCancel: async () => {
      await handleCancel();
    },
  };
};

// Fonction utilitaire pour formater le prix en euros
export const formatPrice = (priceInCents: number | null): string => {
  if (priceInCents === null) return '—';
  return `${(priceInCents / 100).toFixed(2)} €`;
};

// Fonction utilitaire pour obtenir le prix selon la périodicité
export const getPriceByPeriodicity = (
  plan: Pick<SubscriptionPlanData, 'priceMonthly' | 'priceYearly'>,
  periodicity: BillingPeriod,
): number => {
  return periodicity === 'monthly' ? plan.priceMonthly : plan.priceYearly;
};

// Fonction utilitaire pour calculer l'économie annuelle
export const getYearlyDiscount = (plan: Pick<SubscriptionPlanData, 'priceMonthly' | 'priceYearly'>): number | null => {
  if (!plan.priceMonthly || !plan.priceYearly) return null;

  const monthlyYearlyTotal = plan.priceMonthly * 12;
  const discount = monthlyYearlyTotal - plan.priceYearly;

  return Math.round((discount / monthlyYearlyTotal) * 100);
};
